import React, { useEffect, useRef, useMemo, useState } from 'react';
const mono  = { fontFamily: 'JetBrains Mono, monospace' };
const panel = { background: '#141720', border: '1px solid #ffffff14', borderRadius: 8, padding: '14px 16px' };
const SEV_COLOR = { CRITICAL: '#ff0040', HIGH: '#ff3b4e', MEDIUM: '#f59e0b', LOW: '#10b981' };

// Smart City sensor zones — global smart city locations
const SENSOR_ZONES = [
  { name: 'Traffic Hub — NYC',      lat: 40.7128,  lng: -74.0060,  type: 'TRAFFIC' },
  { name: 'Power Grid — London',    lat: 51.5074,  lng: -0.1278,   type: 'SCADA'   },
  { name: 'Smart Meters — Tokyo',   lat: 35.6762,  lng: 139.6503,  type: 'METER'   },
  { name: 'CCTV Cluster — Dubai',   lat: 25.2048,  lng: 55.2708,   type: 'CAMERA'  },
  { name: 'Water Control — Sydney', lat: -33.8688, lng: 151.2093,  type: 'WATER'   },
  { name: 'Traffic Hub — Berlin',   lat: 52.5200,  lng: 13.4050,   type: 'TRAFFIC' },
  { name: 'SCADA — Singapore',      lat: 1.3521,   lng: 103.8198,  type: 'SCADA'   },
];

// Map each IP subnet to a real global city — spread attacks worldwide
const SUBNET_GEO = {
  '10.0':    { cities: [
    { lat: 40.7128,  lng: -74.0060  }, // New York
    { lat: 40.7580,  lng: -73.9855  }, // Manhattan
    { lat: 40.6892,  lng: -74.0445  }, // Brooklyn
    { lat: 40.7282,  lng: -73.7949  }, // Queens
  ]},
  '10.10':   { cities: [
    { lat: 51.5074,  lng: -0.1278   }, // London
    { lat: 51.5033,  lng: -0.1195   }, // London City
    { lat: 51.4816,  lng: -0.1927   }, // Chelsea
    { lat: 51.5246,  lng: -0.0900   }, // Shoreditch
  ]},
  '10.20':   { cities: [
    { lat: 35.6762,  lng: 139.6503  }, // Tokyo
    { lat: 35.6895,  lng: 139.6917  }, // Shinjuku
    { lat: 35.6586,  lng: 139.7454  }, // Shibuya
    { lat: 35.7101,  lng: 139.8107  }, // Akihabara
  ]},
  '192.168': { cities: [
    { lat: 25.2048,  lng: 55.2708   }, // Dubai
    { lat: 25.1972,  lng: 55.2744   }, // Downtown Dubai
    { lat: 25.2285,  lng: 55.3273   }, // Deira
    { lat: 25.0657,  lng: 55.1713   }, // JBR
  ]},
  '172.16':  { cities: [
    { lat: -33.8688, lng: 151.2093  }, // Sydney
    { lat: -33.8600, lng: 151.2058  }, // CBD
    { lat: -33.8830, lng: 151.2162  }, // Surry Hills
    { lat: -33.9100, lng: 151.1900  }, // Alexandria
  ]},
};

function ipToLatLng(ip) {
  if (!ip) return null;
  const parts = ip.split('.').map(Number);
  if (parts.length !== 4) return null;

  const prefix = `${parts[0]}.${parts[1]}`;
  const geo = SUBNET_GEO[prefix];
  if (!geo) return null;

  // Pick city based on third octet, add small jitter from fourth octet
  const cityIdx = parts[2] % geo.cities.length;
  const base = geo.cities[cityIdx];
  const jitter = (n, scale) => ((n % 20) - 10) * scale;
  return {
    lat: base.lat + jitter(parts[3], 0.008),
    lng: base.lng + jitter(parts[3], 0.010),
  };
}

function waitForLeaflet(callback, retries = 20, delay = 300) {
  if (window.L) { callback(window.L); return; }
  if (retries <= 0) { console.warn('Leaflet failed to load after retries'); return; }
  setTimeout(() => waitForLeaflet(callback, retries - 1, delay), delay);
}

const DEVICE_TYPE = {
  '10.0':    'SCADA Node',
  '10.10':   'Traffic Sensor',
  '10.20':   'IP Camera',
  '192.168': 'Smart Meter',
  '172.16':  'IoT Gateway',
};

function getDeviceType(ip) {
  if (!ip) return 'Unknown';
  const p = ip.split('.');
  return DEVICE_TYPE[`${p[0]}.${p[1]}`] || 'Unknown';
}

const CITY_NAME = {
  '10.0':    'New York',
  '10.10':   'London',
  '10.20':   'Tokyo',
  '192.168': 'Dubai',
  '172.16':  'Sydney',
};

function getCity(ip) {
  if (!ip) return '';
  const p = ip.split('.');
  return CITY_NAME[`${p[0]}.${p[1]}`] || '';
}

export default function GeoIPMap({ alerts = [] }) {
  const mapRef         = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef     = useRef([]);
  const [leafletReady, setLeafletReady] = useState(!!window.L);
  const [mapError,     setMapError]     = useState(false);
  const [ipData,       setIpData]       = useState([]);

  // ── Filters ──────────────────────────────────────────────────────────────
  const [sevFilter,  setSevFilter]  = useState('ALL');
  const [cityFilter, setCityFilter] = useState('ALL');
  const [typeFilter, setTypeFilter] = useState('ALL');

  // Fetch full IP summary from backend (covers all 335k+ alerts)
  useEffect(() => {
    const fetchIPs = () => {
      fetch('/api/alerts/ip-summary')
        .then(r => r.json())
        .then(d => setIpData((d.ips || []).map(item => ({
          ...item,
          city:   getCity(item.ip),
          device: getDeviceType(item.ip),
        }))))
        .catch(() => {});
    };
    fetchIPs();
    const t = setInterval(fetchIPs, 15000);
    return () => clearInterval(t);
  }, [alerts]);

  const ipSeverity = ipData.slice(0, 80);

  // Apply filters
  const filtered = useMemo(() => {
    return ipSeverity.filter(({ sev, city, device }) => {
      if (sevFilter  !== 'ALL' && sev    !== sevFilter)  return false;
      if (cityFilter !== 'ALL' && city   !== cityFilter) return false;
      if (typeFilter !== 'ALL' && device !== typeFilter) return false;
      return true;
    });
  }, [ipSeverity, sevFilter, cityFilter, typeFilter]);

  // ── Init map ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (mapInstanceRef.current) return;
    waitForLeaflet((L) => {
      if (!mapRef.current || mapInstanceRef.current) return;
      setLeafletReady(true);
      try {
        const map = L.map(mapRef.current, {
          center: [25, 10],
          zoom: 2,
          zoomControl: true,
          attributionControl: false,
        });
        L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
          maxZoom: 19,
        }).addTo(map);

        const zoneColors = { TRAFFIC:'#3b82f6', SCADA:'#f59e0b', METER:'#8b5cf6', CAMERA:'#06b6d4', WATER:'#10b981' };
        SENSOR_ZONES.forEach(zone => {
          const col  = zoneColors[zone.type] || '#6b7280';
          const icon = L.divIcon({
            className: '',
            html: `<div style="width:20px;height:20px;border:2px solid ${col};border-radius:3px;background:${col}22;display:flex;align-items:center;justify-content:center;font-size:8px;color:${col};font-family:monospace;">${zone.type[0]}</div>`,
            iconSize: [20, 20],
          });
          L.marker([zone.lat, zone.lng], { icon })
            .bindPopup(`<b style="font-family:monospace;font-size:11px">${zone.name}</b><br/><span style="font-size:10px;color:#888">${zone.type} Node</span>`)
            .addTo(map);
        });

        mapInstanceRef.current = map;
        setTimeout(() => map.invalidateSize(), 300);
      } catch (e) {
        console.error('Map init error:', e);
        setMapError(true);
      }
    });
  }, []);

  // ── Update markers on filter change ──────────────────────────────────────
  useEffect(() => {
    const L   = window.L;
    const map = mapInstanceRef.current;
    if (!L || !map) return;

    markersRef.current.forEach(m => map.removeLayer(m));
    markersRef.current = [];

    filtered.forEach(({ ip, sev, attackType, city, device, count = 1 }) => {
      const pos = ipToLatLng(ip);
      if (!pos) return;
      const col  = SEV_COLOR[sev] || '#6b7280';
      const countLabel = count >= 1000000 ? (count/1000000).toFixed(1)+'M' : count >= 1000 ? (count/1000).toFixed(0)+'k' : String(count);
      const size = count > 10000 ? 28 : count > 1000 ? 22 : count > 100 ? 18 : 14;
      const icon = L.divIcon({
        className: '',
        html: `<div style="width:${size}px;height:${size}px;background:${col};border-radius:50%;border:2px solid ${col}88;box-shadow:0 0 8px ${col}88;display:flex;align-items:center;justify-content:center;font-size:${size>18?8:7}px;color:#fff;font-family:monospace;font-weight:700;">${countLabel}</div>`,
        iconSize: [size, size],
        iconAnchor: [size/2, size/2],
      });
      const marker = L.marker([pos.lat, pos.lng], { icon })
        .bindPopup(`
          <div style="font-family:monospace;font-size:11px;min-width:180px;">
            <b style="color:${col}">${sev}</b> · ${ip}<br/>
            <span style="color:#f59e0b;font-weight:700">${count.toLocaleString()} alerts</span><br/>
            <span style="color:#aaa">${attackType || '—'}</span><br/>
            <span style="color:#888">📍 ${city} · ${device}</span>
          </div>`)
        .addTo(map);
      markersRef.current.push(marker);
    });
  }, [filtered, leafletReady]);

  const counts = { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0 };
  filtered.forEach(({ sev }) => { if (counts[sev] !== undefined) counts[sev]++; });

  const cities    = ['ALL', ...Object.values(CITY_NAME)];
  const devices   = ['ALL', 'SCADA Node', 'Traffic Sensor', 'IP Camera', 'Smart Meter', 'IoT Gateway'];
  const severities = ['ALL', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];

  const selStyle = {
    background: '#0d1018', border: '1px solid #ffffff22', borderRadius: 4,
    color: '#9ca3af', padding: '3px 7px', fontSize: 10, ...mono, outline: 'none', cursor: 'pointer',
  };

  return (
    <div style={panel}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 11, color: '#9ca3af', ...mono, letterSpacing: '.1em' }}>SMART CITY THREAT MAP</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Severity filter */}
          <select value={sevFilter} onChange={e => setSevFilter(e.target.value)} style={selStyle}>
            {severities.map(s => <option key={s} value={s}>{s === 'ALL' ? 'All Severities' : s}</option>)}
          </select>
          {/* City filter */}
          <select value={cityFilter} onChange={e => setCityFilter(e.target.value)} style={selStyle}>
            {cities.map(c => <option key={c} value={c}>{c === 'ALL' ? 'All Cities' : c}</option>)}
          </select>
          {/* Device type filter */}
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={selStyle}>
            {devices.map(d => <option key={d} value={d}>{d === 'ALL' ? 'All Devices' : d}</option>)}
          </select>
          {/* Severity counters */}
          {Object.entries(counts).map(([s, n]) => n > 0 && (
            <span key={s} style={{ fontSize: 10, ...mono, color: SEV_COLOR[s], background: `${SEV_COLOR[s]}18`, padding: '2px 7px', borderRadius: 3, border: `1px solid ${SEV_COLOR[s]}33` }}>
              {s}: {n}
            </span>
          ))}
        </div>
      </div>

      {/* City attack summary bar */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
        {Object.entries(CITY_NAME).map(([prefix, city]) => {
          const cityCount = filtered.filter(x => x.city === city).length;
          if (!cityCount) return null;
          return (
            <span key={city} onClick={() => setCityFilter(cityFilter === city ? 'ALL' : city)}
              style={{ fontSize: 9, ...mono, color: '#60a5fa', background: '#60a5fa12', padding: '2px 6px', borderRadius: 3, border: `1px solid ${cityFilter === city ? '#60a5fa' : '#60a5fa22'}`, cursor: 'pointer' }}>
              📍 {city} ({cityCount})
            </span>
          );
        })}
        <span style={{ fontSize: 9, ...mono, color: '#4b5563', marginLeft: 'auto' }}>
          {filtered.length} IPs shown
        </span>
      </div>

      {/* Map */}
      <div ref={mapRef} style={{ height: 320, borderRadius: 6, overflow: 'hidden', background: '#0d1018', position: 'relative' }}>
        {!leafletReady && !mapError && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4b5563', fontSize: 11, ...mono }}>
            Loading map tiles...
          </div>
        )}
        {mapError && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <div style={{ color: '#ff3b4e', fontSize: 11, ...mono }}>Map unavailable</div>
            <div style={{ color: '#4b5563', fontSize: 10, ...mono }}>Add Leaflet CDN to public/index.html</div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 14, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ fontSize: 10, color: '#4b5563', ...mono }}>● Source IP severity</div>
        {[['TRAFFIC','#3b82f6'],['SCADA','#f59e0b'],['CAMERA','#06b6d4'],['METER','#8b5cf6'],['WATER','#10b981']].map(([l,c]) => (
          <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: '#6b7280', ...mono }}>
            <div style={{ width: 8, height: 8, border: `2px solid ${c}`, borderRadius: 2 }} />{l}
          </div>
        ))}
        <div style={{ marginLeft: 'auto', fontSize: 9, ...mono, color: '#374151' }}>
          NYC · London · Tokyo · Dubai · Sydney
        </div>
      </div>
    </div>
  );
}
